import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BookWizard } from './BookWizard';

/**
 * Sub-spec 01b Plan 4 — embed widget shell.
 *
 * Wraps `BookWizard` in a chrome-less layout suitable for cross-origin iframe
 * embedding. The host page mounts an iframe:
 *
 *     <iframe src="https://<tenant>.kennel.app/embed/book?service=boarding"
 *             id="kennel-book-frame" style="width:100%;border:0"></iframe>
 *
 * Behaviour:
 *  - Emits `'kennel-book-wizard:loaded'` on mount (parent can swap a skeleton
 *    once the iframe is up).
 *  - Emits `'kennel-book-wizard:resize'` with the wizard's current height via
 *    ResizeObserver; the companion `embed.js` script (host page) listens for
 *    these and adjusts the iframe height.
 *  - Emits `'kennel-book-wizard:booking-created'` when the wizard reaches the
 *    confirmation page. Parent pages can hook this to fire conversion pixels
 *    or render a thank-you state inline.
 *
 * Cross-origin auth: this widget does NOT establish a customer session on the
 * tenant's main domain. A visitor can browse availability + price inside the
 * iframe with no session; when they reach the booking step the wizard shows
 * its inline magic-link form. The link opens `/embed/book?draft=…` at top
 * level on the tenant host, where the draft is restored from localStorage
 * and the booking completes. Per spec §8 we accept the v1 simplification of
 * "embed is for the funnel; finalisation happens on the main domain" — a
 * future cookie bridge would lift this.
 */
export function EmbedBookWizard() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  // Loaded event — fires once on mount.
  useEffect(() => {
    postToParent({ type: 'kennel-book-wizard:loaded' });
  }, []);

  // Confirmation hook — when the URL transitions to /book/<id>/confirmation
  // inside the iframe, fire the booking-created event up to the parent.
  // Implemented by listening to React Router's location changes.
  useEffect(() => {
    const m = location.pathname.match(/^\/book\/([^/]+)\/confirmation$/);
    if (m && m[1]) {
      postToParent({ type: 'kennel-book-wizard:booking-created', bookingId: m[1] });
    }
  }, [location.pathname]);

  // Resize observer — emit the wizard root's height up to the parent so the
  // host page can grow/shrink the iframe.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const h = el.getBoundingClientRect().height;
      postToParent({ type: 'kennel-book-wizard:resize', height: Math.ceil(h) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={rootRef} className="bg-paper min-h-[100dvh]">
      {/* No BrandedShell wrapper here — the embed is chrome-less by design.
          The wizard itself still wraps in BrandedShell, which on the embed
          path strips down to the brand colors only (see BrandedShell). */}
      <BookWizard />
    </div>
  );
}

function postToParent(message: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  // Only post if we're actually in an iframe. Top-level loads of /embed/book
  // shouldn't broadcast to themselves.
  if (window.parent && window.parent !== window) {
    try {
      window.parent.postMessage(message, '*');
    } catch {
      // Cross-origin parent that doesn't accept '*' — silently drop.
    }
  }
}
